import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { beverages, beverageCategories } from '../../mocks/beverages';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { Link } from 'react-router-dom';

export default function MenuPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [addedItem, setAddedItem] = useState<string | null>(null);
  const { addItem } = useCart();
  const toast = useToast();

  const handleAddToCart = (bev: typeof beverages[0], e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({ id: bev.id, name: bev.name, price: bev.price, image: bev.image, volume: bev.volume });
    setAddedItem(bev.id);
    setTimeout(() => setAddedItem(null), 1500);
    toast.cart(`${bev.name} added!`, `GYD $${bev.price.toLocaleString()} · ${bev.volume}`);
  };

  const filtered = beverages
    .filter((b) => activeCategory === 'all' || b.category === activeCategory)
    .filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return b.reviews - a.reviews;
    });

  const formatPrice = (price: number) => `GYD $${price.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      {/* Page Hero */}
      <div className="relative pt-16 overflow-hidden">
        <div className="bg-[#0D0D0D] py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <span className="inline-block bg-[#FF6B35]/20 text-[#FF6B35] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-widest uppercase">
              Interactive Menu
            </span>
            <h1 className="text-white font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(40px, 6vw, 72px)', letterSpacing: '0.02em' }}>
              Georgetown&apos;s Finest Beverages
            </h1>
            <p className="text-white/60 text-sm max-w-lg">
              Browse 200+ beverages available for fast delivery across Georgetown, Guyana. Filter, sort, and discover your favorites.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Search beverages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-[#FF6B35] transition-colors"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-white rounded-xl border border-gray-200 text-sm text-gray-700 outline-none focus:border-[#FF6B35] cursor-pointer"
          >
            <option value="popular">Most Popular</option>
            <option value="rating">Top Rated</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {beverageCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#FF6B35] hover:text-[#FF6B35]'
              }`}
            >
              {cat.label}
              <span className="ml-1 text-xs opacity-60">
                ({cat.id === 'all' ? beverages.length : beverages.filter(b => b.category === cat.id).length})
              </span>
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-gray-400 text-sm mb-6">
          Showing <strong className="text-gray-800">{filtered.length}</strong> beverages
          {search && <> for &quot;<strong className="text-[#FF6B35]">{search}</strong>&quot;</>}
        </p>

        {/* Product Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-product-shop>
          {filtered.map((bev) => (
            <Link
              key={bev.id}
              to={`/beverage/${bev.id}`}
              className="bg-white rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300 block"
            >
              <div className="relative h-52 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
                <img
                  src={bev.image}
                  alt={bev.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                {bev.badge && (
                  <span className="absolute top-3 left-3 bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-lg">
                    {bev.badge}
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-xs" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700">{bev.rating}</span>
                  <span className="text-xs text-gray-400">({bev.reviews})</span>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-0.5">{bev.name}</h3>
                <p className="text-gray-400 text-xs mb-3">{bev.volume}</p>
                <div className="flex items-center justify-between">
                  <span className="font-black text-gray-900 text-sm">{formatPrice(bev.price)}</span>
                  <button
                    onClick={(e) => handleAddToCart(bev, e)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-white transition-all cursor-pointer ${
                      addedItem === bev.id ? 'bg-green-500 scale-110' : 'bg-[#FF6B35] hover:bg-[#e85d2a]'
                    }`}
                  >
                    <i className={`text-sm ${addedItem === bev.id ? 'ri-check-line' : 'ri-add-line'}`} />
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <div className="w-3 h-3 flex items-center justify-center text-[#FF6B35]">
                    <i className="ri-time-line text-xs" />
                  </div>
                  <span className="text-xs text-gray-400">{bev.deliveryTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-24">
            <div className="w-16 h-16 flex items-center justify-center text-gray-300 mx-auto mb-4">
              <i className="ri-search-line text-5xl" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">No beverages found</h3>
            <p className="text-gray-400 text-sm">Try a different search term or category</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
